/**
 * 満室表示・ソート判定に使う最小構造型（部屋タイプ）。
 * - `unavailableForRange`: 日程フィルタで「1室で連泊を満たせない」（`selectVisibleAreas` が付与）。
 * - `availabilities`: 未取得（構造フェーズ）のことがある。取得済みなら `fromPrice` で満室判定する。
 */
type UnavailableRoomType = {
  unavailableForRange: boolean;
  availabilities?: { fromPrice?: number | null };
};

/**
 * 部屋タイプが「空きなし（満室）」として表示すべきかどうかを確認する。
 * 満室バッジ・opacity・価格非表示・ghost「部屋詳細」ボタン・日付非引き継ぎモーダルはすべて
 * この関数の結果で駆動する。
 *
 * - `unavailableForRange`: 日程フィルタで連泊を通せない（フィルタ時のみ true になりうる）
 * - `fromPrice == null`: 日程フィルタの有無に関わらず、期間内に空きが全く無い（browse-all でも起こる）
 *
 * 構造フェーズ（`availabilities` 未取得）は両条件とも false 相当になり、false を返す
 * （価格ロード完了まで満室表示にしない＝フリッカー防止）。
 */
export function isRoomTypeUnavailable(rt: UnavailableRoomType): boolean {
  return (
    rt.unavailableForRange ||
    (rt.availabilities !== undefined && rt.availabilities.fromPrice == null)
  );
}

/**
 * 部屋タイプ配列を `unavailableForRange` で末尾に振り分ける
 * （空きあり→空きなしの順、各群内は元の順序を維持）。
 *
 * `filter` を2回使って前後に分けるだけの実装で、各群内の元の順序はそのまま保たれる。
 * 元配列は変更しない。
 */
export function sortRoomTypesUnavailableLast<
  T extends { unavailableForRange: boolean },
>(roomTypes: readonly T[]): T[] {
  const available = roomTypes.filter((rt) => !rt.unavailableForRange);
  const unavailable = roomTypes.filter((rt) => rt.unavailableForRange);
  return [...available, ...unavailable];
}

/**
 * 建築タイプグループ配下の部屋タイプが1件以上あり、かつ全件が `unavailableForRange: true` かどうか。
 * 建築タイプグループの非表示判定に使う。
 */
export function isArchitectureTypeGroupUnavailableForRange<
  T extends { unavailableForRange: boolean },
>(roomTypes: readonly T[]): boolean {
  return (
    roomTypes.length > 0 && roomTypes.every((rt) => rt.unavailableForRange)
  );
}

/**
 * 部屋タイプ配列に満室（`unavailableForRange: true`）が1件でもあるかどうか。
 * 「空室のみ表示」トグルの表示可否判定に使う。
 */
export function hasUnavailableRoomTypeForRange<
  T extends { unavailableForRange: boolean },
>(roomTypes: readonly T[]): boolean {
  return roomTypes.some((rt) => rt.unavailableForRange);
}

/**
 * 「空室のみ表示」トグルの状態に応じて表示する部屋タイプ配列を選択する。
 * - `showUnavailable: false`: 満室を除外し、元の順序を維持する
 * - `showUnavailable: true`: `sortRoomTypesUnavailableLast` で空きあり優先に並べ替え、全件返す
 */
export function selectDisplayedRoomTypes<
  T extends { unavailableForRange: boolean },
>(roomTypes: readonly T[], showUnavailable: boolean): T[] {
  if (!showUnavailable) {
    return roomTypes.filter((rt) => !rt.unavailableForRange);
  }
  return sortRoomTypesUnavailableLast(roomTypes);
}
